import { __ } from "@wordpress/i18n";
import {
	useBlockProps,
	useInnerBlocksProps,
	InspectorControls,
} from "@wordpress/block-editor";
import {
	Panel,
	PanelBody,
	RangeControl,
	ToggleControl,
	TextControl,
} from "@wordpress/components";
import { useEffect, useRef, useState } from "@wordpress/element";

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {Element} Element to render.
 */
export default function Edit(props) {
	const { attributes, setAttributes } = props;
	const parallaxCover = useRef(null);
	const [imageLoaded, setImageLoaded] = useState(false);

	function setOption(key, value) {
		setAttributes({ [key]: value });
	}

	const ALLOWED_BLOCKS = ["core/cover"];
	const TEMPLATE = [
		[
			"core/cover",
			{
				lock: {
					remove: true,
					move: true,
				},
			},
		],
	];

	const blocksProps = useBlockProps({
		ref: parallaxCover,
		className: `wpopus-parallax-bg-cover`,
	});

	const { children, ...innerBlocksProps } = useInnerBlocksProps(blocksProps, {
		allowedBlocks: ALLOWED_BLOCKS,
		template: TEMPLATE,
		orientation: "horizontal",
		renderAppender: false,
	});

	const applyScrollParallax = (img) => {
		if (!img || !attributes.enableParallaxScroll) return;

		const imgParent = img.parentElement;
		if (!imgParent) return;

		const editorWrapper = document.querySelector(
			".interface-interface-skeleton__content",
		);
		if (!editorWrapper) return;

		const speed = attributes.parallaxScrollSpeed;
		const handleParallax = () => {
			const imgY =
				imgParent.getBoundingClientRect().top + editorWrapper.scrollTop;
			const winY = editorWrapper.scrollTop;
			const winH = editorWrapper.clientHeight;
			const parentH = imgParent.offsetHeight;
			const imgH = img.offsetHeight;
			const winBottom = winY + winH;

			if (winBottom > imgY && winY < imgY + parentH) {
				const imgBottom = (winBottom - imgY) * speed;
				const imgTop = winH + parentH;
				let imgPercent = (imgBottom / imgTop) * 100 + (50 - speed * 50);

				const maxMove = ((imgH - parentH) / 2) * (100 / imgH);
				imgPercent = Math.max(50 - maxMove, Math.min(imgPercent, 50 + maxMove));

				requestAnimationFrame(() => {
					img.dataset.scrollPercent = imgPercent;
					img.style.top = `${imgPercent}%`;
					img.style.transform = `translate(-50%, -${imgPercent}%)`;
				});
			}
		};

		editorWrapper.addEventListener("scroll", handleParallax, { passive: true });
		return () => editorWrapper.removeEventListener("scroll", handleParallax);
	};

	const applyMouseMoveParallax = (img, parent) => {
		if (!img || !parent || !attributes.enableMouseEffect) return;

		const handleMouseMove = (event) => {
			const { left, top, width, height } = parent.getBoundingClientRect();
			const x = event.clientX - left - width / 2;
			const y = event.clientY - top - height / 2;

			const moveX = (x / width) * 10;
			const moveY = (y / height) * 10;
			const scrollPercent = img.dataset.scrollPercent || 50;

			requestAnimationFrame(() => {
				img.style.transform = `translate(-50%, -${scrollPercent}%) translate(${moveX}px, ${moveY}px)`;
			});
		};

		const resetTransform = () => {
			const scrollPercent = img.dataset.scrollPercent || 50;
			requestAnimationFrame(() => {
				img.style.transform = `translate(-50%, -${scrollPercent}%)`;
			});
		};

		parent.addEventListener("mousemove", handleMouseMove);
		parent.addEventListener("mouseleave", resetTransform);

		return () => {
			parent.removeEventListener("mousemove", handleMouseMove);
			parent.removeEventListener("mouseleave", resetTransform);
		};
	};

	useEffect(() => {
		const blockElement = parallaxCover.current;
		if (!blockElement) return;

		const observer = new MutationObserver(() => {
			const img = blockElement.querySelector(
				".wp-block-cover__image-background",
			);
			if (img) {
				setImageLoaded(true);
			}
		});

		observer.observe(blockElement, { childList: true, subtree: true });
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (!imageLoaded) return;
		const blockElement = parallaxCover.current;
		if (!blockElement) return;

		// const img = blockElement.querySelector(".wp-block-cover__image-background");
		let img = blockElement.querySelector(".wp-block-cover__image-background, .wp-block-cover__video-background");

		if (img) {
			const cleanupScroll = applyScrollParallax(img);
			const cleanupMouse = applyMouseMoveParallax(img, blockElement);

			return () => {
				cleanupScroll?.();
				cleanupMouse?.();
			};
		}
	}, [
		imageLoaded,
		attributes.enableParallaxScroll,
		attributes.enableMouseEffect,
		attributes.parallaxScrollSpeed,
	]);

	return (
		<div {...innerBlocksProps}>
			<InspectorControls>
				<Panel>
					<PanelBody
						title={__("Parallax Scroll Settings", "wpopus")}
						initialOpen={true}
					>
						<fieldset>
							<ToggleControl
								__nextHasNoMarginBottom
								label={__("Enable Parallax Scroll", "wpopus")}
								checked={attributes.enableParallaxScroll}
								onChange={(newValue) => {
									setOption("enableParallaxScroll", newValue);
								}}
							/>
						</fieldset>

						<br />
						<fieldset>
							<ToggleControl
								__nextHasNoMarginBottom
								label={__("Enable Mouse Parallax", "wpopus")}
								checked={attributes.enableMouseEffect}
								onChange={(newValue) => {
									setOption("enableMouseEffect", newValue);
								}}
							/>
						</fieldset>

						<br />
						<fieldset>
							<RangeControl
								__nextHasNoMarginBottom
								__next40pxDefaultSize
								label={__("Scroll Speed", "wpopus")}
								value={attributes.parallaxScrollSpeed}
								onChange={(newValue) => {
									setOption("parallaxScrollSpeed", parseFloat(newValue));
								}}
								min={-1}
								step={0.05}
								max={2}
							/>
						</fieldset>
					</PanelBody>
				</Panel>
			</InspectorControls>
			{children}
		</div>
	);
}
